/**
 * Página do Serviço: IA Comportamental
 * WorkStream - Análise comportamental com Machine Learning
 */
import { Link } from 'react-router-dom'
import { 
  Brain, Sparkles, AlertTriangle, RefreshCw, LineChart,
  ArrowRight, Play, Cpu, Database
} from 'lucide-react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const features = [
  {
    icon: Brain,
    title: 'Machine Learning',
    description: 'Modelos que aprendem continuamente com os dados da sua operação.',
  },
  {
    icon: AlertTriangle,
    title: 'Alertas Preditivos',
    description: 'Antecipe problemas antes que aconteçam. Previsão de churn, riscos e anomalias.',
  },
  {
    icon: Sparkles,
    title: 'Insights Automáticos',
    description: 'IA analisa padrões e sugere ações de melhoria automaticamente.',
  },
  {
    icon: RefreshCw,
    title: 'Auto-Learning',
    description: 'Retreinamento automático semanal. Modelos sempre atualizados.',
  },
  {
    icon: LineChart,
    title: 'Análise de Tendências',
    description: 'Identifique padrões de comportamento ao longo do tempo.',
  },
  {
    icon: Database,
    title: 'Análise de Áudio',
    description: 'Detecta nível de atividade sonora e padrões de trabalho.',
  },
]

const models = [
  {
    name: 'Churn Prediction',
    description: 'Prevê quais colaboradores têm maior chance de sair',
    accuracy: '92%',
    color: 'rose',
  },
  {
    name: 'Performance Prediction',
    description: 'Prevê se metas serão batidas com base em padrões',
    accuracy: '89%',
    color: 'blue',
  },
  {
    name: 'Risk Scoring',
    description: 'Calcula risco de acidentes por comportamento',
    accuracy: '94%',
    color: 'amber',
  },
  {
    name: 'Engagement Score',
    description: 'Mede nível de engajamento e motivação',
    accuracy: '87%',
    color: 'emerald',
  },
]

const stats = [
  { value: '8', label: 'Modelos Ativos' },
  { value: '92%', label: 'Precisão Média' },
  { value: '12M+', label: 'Inferências/Mês' },
  { value: '7d', label: 'Retreinamento' },
]

export default function IAComportamentalPage() {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-br from-emerald-500/20 to-transparent rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
              <Brain className="w-4 h-4" />
              SERVIÇO 3 - IA COMPORTAMENTAL
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              IA que aprende.{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-500 bg-clip-text text-transparent">
                Resultado que aparece.
              </span>
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              Machine Learning que analisa comportamento, prevê problemas e 
              sugere melhorias. Quanto mais usa, mais inteligente fica.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="px-8 py-4 rounded-xl font-semibold text-white transition-all flex items-center gap-2"
                style={{ 
                  background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
                  boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)'
                }}
              >
                Testar Grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 rounded-xl font-semibold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-all flex items-center gap-2">
                <Play className="w-5 h-5" />
                Ver Demo
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <p className="text-3xl font-bold text-emerald-400">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Models */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Modelos de IA em Produção
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Modelos treinados e otimizados para sua operação
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {models.map((model) => (
              <div
                key={model.name}
                className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-emerald-500/30 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <Cpu className={`w-8 h-8 text-${model.color}-400`} />
                  <span className={`px-2 py-1 rounded-full text-xs font-bold bg-${model.color}-500/10 text-${model.color}-400`}>
                    {model.accuracy}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{model.name}</h3>
                <p className="text-sm text-slate-400">{model.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Recursos da IA Comportamental
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Inteligência artificial aplicada à gestão de pessoas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon
              return (
                <div
                  key={feature.title}
                  className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-emerald-500/30 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      
      {/* How it Learns */}
      <section className="py-16 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Como a IA Aprende</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <Database className="w-8 h-8 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">1. Coleta de Dados</h3>
                <p className="text-sm text-slate-400">
                  Câmeras e sensores coletam dados de comportamento anonimizados
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">2. Treinamento</h3>
                <p className="text-sm text-slate-400">
                  Modelos são treinados com seus dados específicos
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">3. Insights</h3>
                <p className="text-sm text-slate-400">
                  IA gera insights e alertas preditivos automaticamente
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-500/10 to-cyan-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Deixe a IA trabalhar por você
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all"
            style={{ 
              background: 'linear-gradient(135deg, #10B981 0%, #06B6D4 100%)',
              boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.39)'
            }}
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

